import { localizarPontoNoMundo, pontosDeAncoragem } from './ancoragem'
import { adicionarObjeto } from './operacoes'
import { posturaDoNo, type NoNaPonta } from './nosPresos'
import type { CenaTabletop, CordaTabletop, GeradorIdTabletop, VetorTabletop } from './tipos'

/**
 * O nó com que a ponta de uma corda se prende no ponto de ancoragem. O
 * instrutor não coloca nó solto na cena: ele leva a corda até o ponto e, ali,
 * escolhe como ela se prende. O nó vira uma peça no percurso da corda, do
 * mesmo jeito que os presets já montam (ponto → nó → resto da corda):
 *
 *  - `noOito` (oito duplo / oito encaixado): a alça do oito no ponto e o nó
 *    logo depois dele, na direção em que a corda segue;
 *  - `noFiel` (volta do fiel): a volta abraça o ponto — o degrau, o banzo, o
 *    mosquetão — e a corda sai dela.
 *
 * A ponta continua referenciando o ponto de ancoragem: avisos, ancoragens e a
 * simulação seguem sabendo onde a corda está presa. TypeScript puro.
 */

export type { NoNaPonta }
export type LadoDaPonta = 'inicio' | 'fim'

/** Cordas que se prendem por nó: fita e talabarte já têm a sua costura e o seu mosquetão. */
const cordaAceitaNo = (corda: CordaTabletop) => corda.funcao !== 'fita' && corda.funcao !== 'talabarte' && corda.percurso.length >= 2

/** Índices da ponta e do vizinho dela no percurso. */
const indices = (corda: CordaTabletop, lado: LadoDaPonta): [number, number] =>
  lado === 'inicio' ? [0, 1] : [corda.percurso.length - 1, corda.percurso.length - 2]

const tipoNoPercurso = (cena: CenaTabletop, corda: CordaTabletop, i: number) =>
  cena.objetos.find((o) => o.id === corda.percurso[i]?.idObjeto)?.tipo

/**
 * A ponta pede um nó? Pede quando ela termina num ponto onde a corda se prende
 * (de ancoragem ou de carga, ou a passagem de um mosquetão) e ainda não há nó
 * ali — nem na ponta nem logo depois dela.
 */
export function pontaPedeNo(cena: CenaTabletop, idCorda: string, lado: LadoDaPonta): boolean {
  const corda = cena.cordas?.find((c) => c.id === idCorda)
  if (!corda || !cordaAceitaNo(corda)) return false
  const [i, vizinho] = indices(corda, lado)
  const tipoPonta = tipoNoPercurso(cena, corda, i)
  const tipoVizinho = tipoNoPercurso(cena, corda, vizinho)
  if (tipoPonta === 'noOito' || tipoPonta === 'noFiel' || tipoVizinho === 'noOito' || tipoVizinho === 'noFiel') return false
  const referencia = corda.percurso[i]!
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  if (!objeto) return false
  if (objeto.tipo === 'mosquetao') return true
  const uso = pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)?.uso
  return uso === 'ancoragem' || uso === 'carga'
}

/** As pontas da corda que pedem nó, na ordem: início, depois fim. */
export function pontasQuePedemNo(cena: CenaTabletop, idCorda: string): readonly LadoDaPonta[] {
  return (['inicio', 'fim'] as const).filter((lado) => pontaPedeNo(cena, idCorda, lado))
}

/** O nó que já prende a ponta, se houver (na ponta ou logo depois dela). */
export function noDaPonta(cena: CenaTabletop, idCorda: string, lado: LadoDaPonta): NoNaPonta | null {
  const corda = cena.cordas?.find((c) => c.id === idCorda)
  if (!corda || corda.percurso.length < 2) return null
  const [i, vizinho] = indices(corda, lado)
  for (const k of [i, vizinho]) {
    const tipo = tipoNoPercurso(cena, corda, k)
    if (tipo === 'noOito' || tipo === 'noFiel') return tipo
  }
  return null
}

/**
 * Amarra a ponta da corda com o nó escolhido: o nó entra no percurso logo
 * depois do ponto de ancoragem, voltado para onde a corda segue. Uma alteração
 * só da cena (um passo de desfazer). Falha se a ponta não pede nó.
 */
export function amarrarPonta(
  cena: CenaTabletop,
  idCorda: string,
  lado: LadoDaPonta,
  no: NoNaPonta,
  gerarId: GeradorIdTabletop,
): { readonly cena: CenaTabletop; readonly idNo: string } {
  if (!pontaPedeNo(cena, idCorda, lado)) throw new Error('Essa ponta não se prende num ponto de ancoragem, ou já tem nó.')
  const corda = cena.cordas!.find((c) => c.id === idCorda)!
  const [i, vizinho] = indices(corda, lado)
  const ponta = localizarPontoNoMundo(cena, corda.percurso[i]!)
  const seguinte = localizarPontoNoMundo(cena, corda.percurso[vizinho]!)
  if (!ponta || !seguinte) throw new Error('O ponto da ponta não existe mais na cena.')
  const { posicao, rumo } = posturaDoNo(ponta, seguinte, no)
  const idNo = gerarId()
  const comNo = adicionarObjeto(cena, { tipo: no, transformacao: { posicao, rotacao: [0, rumo, 0], escala: [1, 1, 1] } }, () => idNo)
  const refNo = { idObjeto: idNo, idPonto: no === 'noOito' ? 'passagem' : 'olhal' }
  const percurso = [...corda.percurso]
  percurso.splice(lado === 'inicio' ? 1 : percurso.length - 1, 0, refNo)
  // o trecho da ponta ao nó nasce esticado; a folga que havia no trecho original fica depois do nó
  const folgas = corda.folgas ? [...corda.folgas] : undefined
  if (folgas && folgas.length > 0) {
    if (lado === 'inicio') folgas.splice(0, 0, 0)
    // no fim, o trecho novo (nó → ponta) é o último; se a lista já cobria o último trecho, ele entra esticado depois
    else if (folgas.length >= percurso.length - 2) folgas.splice(percurso.length - 2, 0, 0)
  }
  const nova: CenaTabletop = {
    ...comNo,
    cordas: (comNo.cordas ?? []).map((c) => (c.id === idCorda ? { ...c, percurso, ...(folgas ? { folgas } : {}) } : c)),
  }
  return { cena: nova, idNo }
}
